<template>
    <div ref="tooltip" :class="rootClasses">
        <transition :name="newAnimation">
            <div
                v-show="active && (isActive || always)"
                ref="content"
                :class="['tooltip-content', contentClass]">
                <template v-if="label">{{ label }}</template>
                <template v-else-if="$slots.content">
                    <slot name="content" />
                </template>
            </div>
        </transition>
        <div
            ref="trigger"
            class="tooltip-trigger"
            :style="triggerStyle"
            @click="onClick"
            @contextmenu="onContextMenu"
            @mouseenter="onHover"
            @focus.capture="onFocus"
            @blur.capture="close"
            @mouseleave="close">
            <slot ref="slot" />
        </div>
    </div>
</template>

<script>
import config from 'buefy/src/utils/config'
import { createAbsoluteElement, removeElement } from 'buefy/src/utils/helpers'

export default {
    name: 'Tooltip',
    props: {
        active: {
            type: Boolean,
            default: true
        },
        type: {
            type: String,
            default: () => config.defaultTooltipType
        },
        label: String,
        delay: {
            type: Number,
            default: () => config.defaultTooltipDelay
        },
        closeDelay: {
            type: Number,
            default: () => config.defaultTooltipCloseDelay
        },
        position: {
            type: String,
            default: 'is-top',
            validator(value) {
                return [
                    'is-top',
                    'is-bottom',
                    'is-left',
                    'is-right'
                ].indexOf(value) > -1
            }
        },
        triggers: {
            type: Array,
            default: () => ['hover']
        },
        always: Boolean,
        square: Boolean,
        dashed: Boolean,
        multilined: Boolean,
        size: {
            type: String,
            default: 'is-medium'
        },
        appendToBody: Boolean,
        animated: {
            type: Boolean,
            default: true
        },
        animation: {
            type: String,
            default: 'fade'
        },
        contentClass: String,
        autoClose: {
            type: [Array, Boolean],
            default: true
        }
    },
    data() {
        return {
            isActive: false,
            triggerStyle: {},
            timer: null,
            resizeObserver: undefined,
            resizeListener: undefined,
            timeOutID: null
        }
    },
    computed: {
        rootClasses() {
            return ['c-tooltip', this.type, this.position, this.size, {
                'is-square': this.square,
                'is-always': this.always,
                'is-multiline': this.multilined,
                'is-dashed': this.dashed
            }]
        },
        newAnimation() {
            return this.animated ? this.animation : undefined
        }
    },
    watch: {
        isActive() {
            this.$emit(this.isActive ? 'open' : 'close')
            if (this.appendToBody) {
                this.updateAppendToBody()
            }
        }
    },
    methods: {
        updateAppendToBody() {
            const tooltip = this.$refs.tooltip
            const trigger = this.$refs.trigger
            if (tooltip && trigger) {
                // update wrapper tooltip
                const tooltipEl = this.$data._bodyEl.children[0]
                tooltipEl.classList.forEach((item) => tooltipEl.classList.remove(item))
                if (
                    this.$vnode &&
                    this.$vnode.data &&
                    this.$vnode.data.staticClass
                ) {
                    tooltipEl.classList.add(this.$vnode.data.staticClass)
                }
                this.rootClasses.forEach((item) => {
                    if (typeof item === 'object') {
                        for (const key in item) {
                            if (item[key]) {
                                tooltipEl.classList.add(key)
                            }
                        }
                    } else {
                        tooltipEl.classList.add(item)
                    }
                })

                const rect = trigger.getBoundingClientRect()

                const top = rect.top + window.scrollY
                const left = rect.left + window.scrollX

                // `tooltipEl` will be placed relative to `wrapper`
                // because `wrapper` should create a stacking context
                // as its z-index is non-auto
                tooltipEl.style.position = 'absolute'
                switch (this.position) {
                    case 'is-top':
                        tooltipEl.style.width = `${trigger.clientWidth}px`
                        tooltipEl.style.height = '0px'
                        tooltipEl.style.top = '0px'
                        tooltipEl.style.left = '0px'
                        break
                    case 'is-bottom':
                        tooltipEl.style.width = `${trigger.clientWidth}px`
                        tooltipEl.style.height = '0px'
                        tooltipEl.style.top = `${trigger.clientHeight}px`
                        tooltipEl.style.left = '0px'
                        break
                    case 'is-left':
                        tooltipEl.style.width = '0px'
                        tooltipEl.style.height = `${trigger.clientHeight}px`
                        tooltipEl.style.top = '0px'
                        tooltipEl.style.left = '0px'
                        break
                    case 'is-right':
                        tooltipEl.style.width = '0px'
                        tooltipEl.style.height = `${trigger.clientHeight}px`
                        tooltipEl.style.top = '0px'
                        tooltipEl.style.left = `${trigger.clientWidth}px`
                        break
                }

                const wrapper = this.$data._bodyEl
                wrapper.style.position = 'absolute'
                wrapper.style.top = `${top}px`
                wrapper.style.left = `${left}px`
                wrapper.style.width = '0px'
                wrapper.style.zIndex = this.isActive || this.always ? '99' : '-1'
                this.triggerStyle = {
                    zIndex: this.isActive || this.always ? '100' : undefined
                }
            }
        },
        onClick() {
            if (this.triggers.indexOf('click') < 0) return
            // if not active, toggle after clickOutside event
            // this fixes toggling programmatic
            this.$nextTick(() => {
                this.timeOutID = setTimeout(() => this.open())
            })
        },
        onHover() {
            if (this.triggers.indexOf('hover') < 0) return
            this.open()
        },
        onContextMenu(e) {
            if (this.triggers.indexOf('contextmenu') < 0) return
            e.preventDefault()
            this.open()
        },
        onFocus() {
            if (this.triggers.indexOf('focus') < 0) return
            this.open()
        },
        open() {
            if (this.delay) {
                this.timer = setTimeout(() => {
                    this.isActive = true
                    this.timer = null
                }, this.delay)
            } else {
                this.isActive = true
            }
        },
        close() {
            if (typeof this.autoClose === 'boolean') {
                if (this.autoClose && this.timer) clearTimeout(this.timer)
                if (this.closeDelay) {
                    this.timer = setTimeout(() => {
                        this.isActive = !this.autoClose
                        this.timer = null
                    }, this.closeDelay)
                } else {
                    this.isActive = !this.autoClose
                }
            }
        },
        /**
        * Close tooltip if clicked outside.
        */
        clickedOutside(event) {
            if (this.isActive) {
                if (Array.isArray(this.autoClose)) {
                    if (this.autoClose.includes('outside')) {
                        if (!this.isInWhiteList(event.target)) {
                            this.isActive = false
                            return
                        }
                    }
                    if (this.autoClose.includes('inside')) {
                        if (this.isInWhiteList(event.target)) this.isActive = false
                    }
                }
            }
        },
        /**
         * Keypress event that is bound to the document
         */
        keyPress({ key }) {
            if (this.isActive && (key === 'Escape' || key === 'Esc')) {
                if (Array.isArray(this.autoClose)) {
                    if (this.autoClose.indexOf('escape') >= 0) this.isActive = false
                }
            }
        },
        /**
        * White-listed items to not close when clicked.
        */
        isInWhiteList(el) {
            if (el === this.$refs.content) return true
            // All chidren from content
            if (this.$refs.content !== undefined) {
                const children = this.$refs.content.querySelectorAll('*')
                for (const child of children) {
                    if (el === child) {
                        return true
                    }
                }
            }
            return false
        }
    },
    mounted() {
        if (this.appendToBody && typeof window !== 'undefined') {
            this.controller = new window.AbortController()
            this.$data._bodyEl = createAbsoluteElement(this.$refs.content)
            this.updateAppendToBody()
            // updates the tooltip position if the tooltip is inside
            // `.animation-content`
            const animation = this.$el.closest('.animation-content')
            if (animation != null) {
                const listener = () => {
                    this.updateAppendToBody()
                    animation.removeEventListener('transitionend', listener)
                }
                animation.addEventListener('transitionend', listener, {
                    signal: this.controller.signal
                })
            }
            // observes changes in the window size
            this.resizeListener = () => this.updateAppendToBody()
            window.addEventListener('resize', this.resizeListener)
            // observes changes in the size of the immediate parent
            this.resizeObserver = new ResizeObserver(this.resizeListener)
            if (this.$el.parentNode != null && this.$el.parentNode.nodeType === Node.ELEMENT_NODE) {
                this.resizeObserver.observe(this.$el.parentNode)
            }
        }
    },
    created() {
        if (typeof window !== 'undefined') {
            document.addEventListener('click', this.clickedOutside)
            document.addEventListener('keyup', this.keyPress)
        }
    },
    beforeDestroy() {
        if (typeof window !== 'undefined') {
            document.removeEventListener('click', this.clickedOutside)
            document.removeEventListener('keyup', this.keyPress)
        }
        if (this.resizeListener != null) {
            window.removeEventListener('resize', this.resizeListener)
        }
        if (this.resizeObserver != null) {
            this.resizeObserver.disconnect()
        }
        if (this.appendToBody) {
            removeElement(this.$data._bodyEl)
        }
        if (this.controller != null) {
            this.controller.abort()
        }
        clearTimeout(this.timer)
        clearTimeout(this.timeOutID)
    }
}
</script>
<style scoped lang="scss">
.c-tooltip {
    position: relative;
    display: inline-flex
}

.c-tooltip.is-top .tooltip-content {
    top: auto;
    right: auto;
    bottom: calc(100% + 7px);
    left: 50%;
    transform: translateX(-50%)
}

.c-tooltip.is-top .tooltip-content:before {
    top: 100%;
    right: auto;
    bottom: auto;
    left: 50%;
    transform: translateX(-50%);
    border-top: 5px solid #7957d5;
    border-right: 5px solid transparent;
    border-left: 5px solid transparent
}

.c-tooltip.is-top.is-white .tooltip-content:before {
    border-top-color: #fff
}

.c-tooltip.is-top.is-black .tooltip-content:before {
    border-top-color: #0a0a0a
}

.c-tooltip.is-top.is-light .tooltip-content:before {
    border-top-color: #f5f5f5
}

.c-tooltip.is-top.is-grey .tooltip-content:before {
    border-top-color: #7a7a7a
}

.c-tooltip.is-top.is-dark .tooltip-content:before {
    border-top-color: #363636
}

.c-tooltip.is-top.is-primary .tooltip-content:before {
    border-top-color: #7957d5
}

.c-tooltip.is-top.is-primary.is-light .tooltip-content:before {
    border-top-color: #f2effb
}

.c-tooltip.is-top.is-info .tooltip-content:before {
    border-top-color: #3e8ed0
}

.c-tooltip.is-top.is-info.is-light .tooltip-content:before {
    border-top-color: #eff5fb
}

.c-tooltip.is-top.is-success .tooltip-content:before {
    border-top-color: #48c78e
}

.c-tooltip.is-top.is-success.is-light .tooltip-content:before {
    border-top-color: #effaf5
}

.c-tooltip.is-top.is-warning .tooltip-content:before {
    border-top-color: #ffe08a
}

.c-tooltip.is-top.is-warning.is-light .tooltip-content:before {
    border-top-color: #fffaeb
}

.c-tooltip.is-top.is-danger .tooltip-content:before {
    border-top-color: #f14668
}

.c-tooltip.is-top.is-danger.is-light .tooltip-content:before {
    border-top-color: #feecf0
}

.c-tooltip.is-top.is-link .tooltip-content:before {
    border-top-color: #485fc7
}

.c-tooltip.is-top.is-link.is-light .tooltip-content:before {
    border-top-color: #eff1fa
}

.c-tooltip.is-top.is-twitter .tooltip-content:before {
    border-top-color: #4099ff
}

.c-tooltip.is-top.is-twitter.is-light .tooltip-content:before {
    border-top-color: #ebf4ff
}

.c-tooltip.is-top.is-discord .tooltip-content:before {
    border-top-color: #7289da
}

.c-tooltip.is-top.is-discord.is-light .tooltip-content:before {
    border-top-color: #eff1fb
}

.c-tooltip.is-top.is-bulma .tooltip-content:before {
    border-top-color: #00d1b2
}

.c-tooltip.is-top.is-bulma.is-light .tooltip-content:before {
    border-top-color: #ebfffc
}

.c-tooltip.is-top.is-vuetelemetry .tooltip-content:before {
    border-top-color: #0bdca0
}

.c-tooltip.is-top.is-vuetelemetry.is-light .tooltip-content:before {
    border-top-color: #ecfef9
}

.c-tooltip.is-right .tooltip-content {
    top: 50%;
    right: auto;
    bottom: auto;
    left: calc(100% + 7px);
    transform: translateY(-50%)
}

.c-tooltip.is-right .tooltip-content:before {
    top: 50%;
    right: 100%;
    bottom: auto;
    left: auto;
    transform: translateY(-50%);
    border-top: 5px solid transparent;
    border-right: 5px solid #7957d5;
    border-bottom: 5px solid transparent
}

.c-tooltip.is-right.is-white .tooltip-content:before {
    border-right-color: #fff
}

.c-tooltip.is-right.is-black .tooltip-content:before {
    border-right-color: #0a0a0a
}

.c-tooltip.is-right.is-light .tooltip-content:before {
    border-right-color: #f5f5f5
}

.c-tooltip.is-right.is-grey .tooltip-content:before {
    border-right-color: #7a7a7a
}

.c-tooltip.is-right.is-dark .tooltip-content:before {
    border-right-color: #363636
}

.c-tooltip.is-right.is-primary .tooltip-content:before {
    border-right-color: #7957d5
}

.c-tooltip.is-right.is-primary.is-light .tooltip-content:before {
    border-right-color: #f2effb
}

.c-tooltip.is-right.is-info .tooltip-content:before {
    border-right-color: #3e8ed0
}

.c-tooltip.is-right.is-info.is-light .tooltip-content:before {
    border-right-color: #eff5fb
}

.c-tooltip.is-right.is-success .tooltip-content:before {
    border-right-color: #48c78e
}

.c-tooltip.is-right.is-success.is-light .tooltip-content:before {
    border-right-color: #effaf5
}

.c-tooltip.is-right.is-warning .tooltip-content:before {
    border-right-color: #ffe08a
}

.c-tooltip.is-right.is-warning.is-light .tooltip-content:before {
    border-right-color: #fffaeb
}

.c-tooltip.is-right.is-danger .tooltip-content:before {
    border-right-color: #f14668
}

.c-tooltip.is-right.is-danger.is-light .tooltip-content:before {
    border-right-color: #feecf0
}

.c-tooltip.is-right.is-link .tooltip-content:before {
    border-right-color: #485fc7
}

.c-tooltip.is-right.is-link.is-light .tooltip-content:before {
    border-right-color: #eff1fa
}

.c-tooltip.is-right.is-twitter .tooltip-content:before {
    border-right-color: #4099ff
}

.c-tooltip.is-right.is-twitter.is-light .tooltip-content:before {
    border-right-color: #ebf4ff
}

.c-tooltip.is-right.is-discord .tooltip-content:before {
    border-right-color: #7289da
}

.c-tooltip.is-right.is-discord.is-light .tooltip-content:before {
    border-right-color: #eff1fb
}

.c-tooltip.is-right.is-bulma .tooltip-content:before {
    border-right-color: #00d1b2
}

.c-tooltip.is-right.is-bulma.is-light .tooltip-content:before {
    border-right-color: #ebfffc
}

.c-tooltip.is-right.is-vuetelemetry .tooltip-content:before {
    border-right-color: #0bdca0
}

.c-tooltip.is-right.is-vuetelemetry.is-light .tooltip-content:before {
    border-right-color: #ecfef9
}

.c-tooltip.is-bottom .tooltip-content {
    top: calc(100% + 7px);
    right: auto;
    bottom: auto;
    left: 50%;
    transform: translateX(-50%)
}

.c-tooltip.is-bottom .tooltip-content:before {
    top: auto;
    right: auto;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-right: 5px solid transparent;
    border-bottom: 5px solid #7957d5;
    border-left: 5px solid transparent
}

.c-tooltip.is-bottom.is-white .tooltip-content:before {
    border-bottom-color: #fff
}

.c-tooltip.is-bottom.is-black .tooltip-content:before {
    border-bottom-color: #0a0a0a
}

.c-tooltip.is-bottom.is-light .tooltip-content:before {
    border-bottom-color: #f5f5f5
}

.c-tooltip.is-bottom.is-grey .tooltip-content:before {
    border-bottom-color: #7a7a7a
}

.c-tooltip.is-bottom.is-dark .tooltip-content:before {
    border-bottom-color: #363636
}

.c-tooltip.is-bottom.is-primary .tooltip-content:before {
    border-bottom-color: #7957d5
}

.c-tooltip.is-bottom.is-primary.is-light .tooltip-content:before {
    border-bottom-color: #f2effb
}

.c-tooltip.is-bottom.is-info .tooltip-content:before {
    border-bottom-color: #3e8ed0
}

.c-tooltip.is-bottom.is-info.is-light .tooltip-content:before {
    border-bottom-color: #eff5fb
}

.c-tooltip.is-bottom.is-success .tooltip-content:before {
    border-bottom-color: #48c78e
}

.c-tooltip.is-bottom.is-success.is-light .tooltip-content:before {
    border-bottom-color: #effaf5
}

.c-tooltip.is-bottom.is-warning .tooltip-content:before {
    border-bottom-color: #ffe08a
}

.c-tooltip.is-bottom.is-warning.is-light .tooltip-content:before {
    border-bottom-color: #fffaeb
}

.c-tooltip.is-bottom.is-danger .tooltip-content:before {
    border-bottom-color: #f14668
}

.c-tooltip.is-bottom.is-danger.is-light .tooltip-content:before {
    border-bottom-color: #feecf0
}

.c-tooltip.is-bottom.is-link .tooltip-content:before {
    border-bottom-color: #485fc7
}

.c-tooltip.is-bottom.is-link.is-light .tooltip-content:before {
    border-bottom-color: #eff1fa
}

.c-tooltip.is-bottom.is-twitter .tooltip-content:before {
    border-bottom-color: #4099ff
}

.c-tooltip.is-bottom.is-twitter.is-light .tooltip-content:before {
    border-bottom-color: #ebf4ff
}

.c-tooltip.is-bottom.is-discord .tooltip-content:before {
    border-bottom-color: #7289da
}

.c-tooltip.is-bottom.is-discord.is-light .tooltip-content:before {
    border-bottom-color: #eff1fb
}

.c-tooltip.is-bottom.is-bulma .tooltip-content:before {
    border-bottom-color: #00d1b2
}

.c-tooltip.is-bottom.is-bulma.is-light .tooltip-content:before {
    border-bottom-color: #ebfffc
}

.c-tooltip.is-bottom.is-vuetelemetry .tooltip-content:before {
    border-bottom-color: #0bdca0
}

.c-tooltip.is-bottom.is-vuetelemetry.is-light .tooltip-content:before {
    border-bottom-color: #ecfef9
}

.c-tooltip.is-left .tooltip-content {
    top: 50%;
    right: calc(100% + 7px);
    bottom: auto;
    left: auto;
    transform: translateY(-50%)
}

.c-tooltip.is-left .tooltip-content:before {
    top: 50%;
    right: auto;
    bottom: auto;
    left: 100%;
    transform: translateY(-50%);
    border-top: 5px solid transparent;
    border-bottom: 5px solid transparent;
    border-left: 5px solid #7957d5
}

.c-tooltip.is-left.is-white .tooltip-content:before {
    border-left-color: #fff
}

.c-tooltip.is-left.is-black .tooltip-content:before {
    border-left-color: #0a0a0a
}

.c-tooltip.is-left.is-light .tooltip-content:before {
    border-left-color: #f5f5f5
}

.c-tooltip.is-left.is-grey .tooltip-content:before {
    border-left-color: #7a7a7a
}

.c-tooltip.is-left.is-dark .tooltip-content:before {
    border-left-color: #363636
}

.c-tooltip.is-left.is-primary .tooltip-content:before {
    border-left-color: #7957d5
}

.c-tooltip.is-left.is-primary.is-light .tooltip-content:before {
    border-left-color: #f2effb
}

.c-tooltip.is-left.is-info .tooltip-content:before {
    border-left-color: #3e8ed0
}

.c-tooltip.is-left.is-info.is-light .tooltip-content:before {
    border-left-color: #eff5fb
}

.c-tooltip.is-left.is-success .tooltip-content:before {
    border-left-color: #48c78e
}

.c-tooltip.is-left.is-success.is-light .tooltip-content:before {
    border-left-color: #effaf5
}

.c-tooltip.is-left.is-warning .tooltip-content:before {
    border-left-color: #ffe08a
}

.c-tooltip.is-left.is-warning.is-light .tooltip-content:before {
    border-left-color: #fffaeb
}

.c-tooltip.is-left.is-danger .tooltip-content:before {
    border-left-color: #f14668
}

.c-tooltip.is-left.is-danger.is-light .tooltip-content:before {
    border-left-color: #feecf0
}

.c-tooltip.is-left.is-link .tooltip-content:before {
    border-left-color: #485fc7
}

.c-tooltip.is-left.is-link.is-light .tooltip-content:before {
    border-left-color: #eff1fa
}

.c-tooltip.is-left.is-twitter .tooltip-content:before {
    border-left-color: #4099ff
}

.c-tooltip.is-left.is-twitter.is-light .tooltip-content:before {
    border-left-color: #ebf4ff
}

.c-tooltip.is-left.is-discord .tooltip-content:before {
    border-left-color: #7289da
}

.c-tooltip.is-left.is-discord.is-light .tooltip-content:before {
    border-left-color: #eff1fb
}

.c-tooltip.is-left.is-bulma .tooltip-content:before {
    border-left-color: #00d1b2
}

.c-tooltip.is-left.is-bulma.is-light .tooltip-content:before {
    border-left-color: #ebfffc
}

.c-tooltip.is-left.is-vuetelemetry .tooltip-content:before {
    border-left-color: #0bdca0
}

.c-tooltip.is-left.is-vuetelemetry.is-light .tooltip-content:before {
    border-left-color: #ecfef9
}

.c-tooltip .tooltip-content {
    width: auto;
    padding: .35rem .75rem;
    border-radius: 6px;
    font-size: .85rem;
    font-weight: 400;
    box-shadow: 0 1px 2px 1px rgba(0,1,0,.2);
    z-index: 38;
    white-space: nowrap;
    position: absolute
}

.c-tooltip .tooltip-content:before {
    position: absolute;
    content: "";
    pointer-events: none;
    z-index: 38
}

.c-tooltip .tooltip-trigger {
    width: 100%
}

.c-tooltip.is-white .tooltip-content {
    background: #fff;
    color: #0a0a0a
}

.c-tooltip.is-black .tooltip-content {
    background: #0a0a0a;
    color: #fff
}

.c-tooltip.is-light .tooltip-content {
    background: #f5f5f5;
    color: rgba(0,0,0,.7)
}

.c-tooltip.is-grey .tooltip-content {
    background: #7a7a7a;
    color: rgba(0,0,0,.7)
}

.c-tooltip.is-dark .tooltip-content {
    background: #363636;
    color: #fff
}

.c-tooltip.is-primary .tooltip-content {
    background: #7957d5;
    color: #fff
}

.c-tooltip.is-primary.is-light .tooltip-content {
    background: #f2effb;
    color: #552fbc
}

.c-tooltip.is-info .tooltip-content {
    background: #3e8ed0;
    color: #fff
}

.c-tooltip.is-info.is-light .tooltip-content {
    background: #eff5fb;
    color: #296fa8
}

.c-tooltip.is-success .tooltip-content {
    background: #48c78e;
    color: #fff
}

.c-tooltip.is-success.is-light .tooltip-content {
    background: #effaf5;
    color: #257953
}

.c-tooltip.is-warning .tooltip-content {
    background: #ffe08a;
    color: rgba(0,0,0,.7)
}

.c-tooltip.is-warning.is-light .tooltip-content {
    background: #fffaeb;
    color: #946c00
}

.c-tooltip.is-danger .tooltip-content {
    background: #f14668;
    color: #fff
}

.c-tooltip.is-danger.is-light .tooltip-content {
    background: #feecf0;
    color: #cc0f35
}

.c-tooltip.is-link .tooltip-content {
    background: #485fc7;
    color: #fff
}

.c-tooltip.is-link.is-light .tooltip-content {
    background: #eff1fa;
    color: #3850b7
}

.c-tooltip.is-twitter .tooltip-content {
    background: #4099ff;
    color: #fff
}

.c-tooltip.is-twitter.is-light .tooltip-content {
    background: #ebf4ff;
    color: #005ac2
}

.c-tooltip.is-discord .tooltip-content {
    background: #7289da;
    color: #fff
}

.c-tooltip.is-discord.is-light .tooltip-content {
    background: #eff1fb;
    color: #2a45a2
}

.c-tooltip.is-bulma .tooltip-content {
    background: #00d1b2;
    color: #fff
}

.c-tooltip.is-bulma.is-light .tooltip-content {
    background: #ebfffc;
    color: #00947e
}

.c-tooltip.is-vuetelemetry .tooltip-content {
    background: #0bdca0;
    color: #fff
}

.c-tooltip.is-vuetelemetry.is-light .tooltip-content {
    background: #ecfef9;
    color: #078d66
}

.c-tooltip.is-always .tooltip-content,.c-tooltip.is-always .tooltip-content:before {
    opacity: 1;
    visibility: visible
}

.c-tooltip.is-multiline .tooltip-content {
    display: flex-block;
    text-align: center;
    white-space: normal
}

.c-tooltip.is-multiline.is-small .tooltip-content {
    width: 180px
}

.c-tooltip.is-multiline.is-medium .tooltip-content {
    width: 240px
}

.c-tooltip.is-multiline.is-large .tooltip-content {
    width: 300px
}

.c-tooltip.is-dashed .tooltip-trigger {
    border-bottom: 1px dashed #b5b5b5;
    cursor: default
}

.c-tooltip.is-square .tooltip-content {
    border-radius: 0
}
</style>